import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { DialogService } from 'src/app/services/dialog.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { MiscEventEntityComponent } from '../misc-event-entity/misc-event-entity.component';

/**
 * One row of the manage table: the report's parsed-event shape plus the
 * in-use counts the list endpoint returns.
 */
export interface ManagedMiscEvent {
  id: string;
  title: string;
  packageCode: string;
  dateStart: string;
  dateEnd: string;
  systemWide: boolean;
  linkedPackages: number;
  linkedDeductions: number;
}

/**
 * "Manage Events" dialog for misc_event entities.
 *
 * An event is a tag catalog packages point at, and the Miscellaneous Services
 * Report scopes itself to one (a mode, not a filter). Create/edit/delete chrome
 * used to sit in the report's Filter panel; it lives here instead, alongside
 * the in-use counts that tell staff up front why a delete is unavailable.
 *
 * The edit form itself is not duplicated: rows hand off to the existing
 * MiscEventEntityComponent in 'edit' mode.
 */
@Component({
  selector: 'app-frm-manage-misc-events',
  templateUrl: './frm-manage-misc-events.component.html',
  styleUrls: ['./frm-manage-misc-events.component.css']
})
export class FrmManageMiscEventsComponent implements OnInit {

  constructor(
    @Inject(MAT_DIALOG_DATA) public _injectedDialogData: any,
    private dialogRef: MatDialogRef<FrmManageMiscEventsComponent>,
    public _dialogService: DialogService,
    public _drupalRESTService: DrupalRESTService,
    public _permissionsService: PermissionsService,
    public _taxonomyService: TaxonomyService,
    public _snackBar: MatSnackBar,
  ) { }

  events: ManagedMiscEvent[] = [];
  showProgressSpinner = false;
  errorMessage = '';

  /** Whether anything was created/edited/deleted while the dialog was open. */
  changed = false;

  /** Row currently awaiting delete confirmation, and the in-flight delete. */
  pendingDeleteId: string | null = null;
  deletingId: string | null = null;

  displayedColumns: string[] = ['title', 'packageCode', 'dates', 'linkedPackages', 'actions'];

  /**
   * Rendered by an expansion row rather than a column: mat-table owns its <tr>
   * elements via matRowDef, so the delete confirmation has to be a row the
   * table itself knows about.
   */
  confirmColumns: string[] = ['confirmDelete'];

  /**
   * mat-table wants a stable dataSource. A getter returning a fresh array on
   * every change-detection pass would re-render the table each cycle, so the
   * two groups are assigned once per list load instead.
   */
  franchiseDataSource = new MatTableDataSource<ManagedMiscEvent>([]);
  studioDataSource = new MatTableDataSource<ManagedMiscEvent>([]);

  ngOnInit(): void {
    this.loadEvents();
  }

  /**
   * Only the row awaiting confirmation renders its expansion row; every other
   * row's confirmation stays collapsed out of the table entirely. mat-table
   * calls the `when` predicate unbound, hence the arrow field.
   */
  isConfirmDeleteRow = (index: number, row: ManagedMiscEvent): boolean => {
    return this.isPendingDelete(row);
  };

  /** The predicate itself, reachable as a normal method. */
  isPendingDelete(row: ManagedMiscEvent): boolean {
    return !!this.pendingDeleteId && row?.id === this.pendingDeleteId;
  }

  /** Push the current grouping into the two table data sources. */
  syncDataSources(): void {
    this.franchiseDataSource.data = this.franchiseEvents;
    this.studioDataSource.data = this.studioEvents;
  }

  /** Result handed back to the report so it can refresh its own selector. */
  get closeData(): { changed: boolean } {
    return { changed: this.changed };
  }

  /** Franchise-wide events, manageable by super admins only. */
  get franchiseEvents(): ManagedMiscEvent[] {
    return this.events.filter(event => event.systemWide);
  }

  /** This studio's own events. */
  get studioEvents(): ManagedMiscEvent[] {
    return this.events.filter(event => !event.systemWide);
  }

  /**
   * @param keepErrorMessage
   *   Set when the refresh is a follow-up to a failure whose message must stay
   *   on screen (the 409 refusal explains what to unlink).
   */
  loadEvents(keepErrorMessage = false): void {
    this.showProgressSpinner = true;
    if (!keepErrorMessage) {
      this.errorMessage = '';
    }
    this._drupalRESTService.httpGET('/api/misc-event/list')
      .subscribe(
        (data: any) => {
          this.showProgressSpinner = false;
          this.events = this.parseEvents(data);
          this.syncDataSources();
        },
        (error: any) => {
          this.showProgressSpinner = false;
          this.errorMessage = error?.error?.message || 'Failed to load events.';
        }
      );
  }

  /**
   * The list endpoint's raw shape converted to the report's parsed-row shape,
   * which is also what MiscEventEntityComponent expects as fieldsData.
   */
  parseEvents(data: any): ManagedMiscEvent[] {
    return (data ?? []).map((event: any) => ({
      id: event?.id?.toString(),
      title: event?.title ?? '(untitled event)',
      packageCode: event?.package_code?.toString(),
      dateStart: event?.date_start,
      dateEnd: event?.date_end,
      systemWide: !!event?.system_wide,
      linkedPackages: Number(event?.linked_packages ?? 0),
      linkedDeductions: Number(event?.linked_deductions ?? 0),
    })).filter((event: any) => event.id);
  }

  /** Human-readable package code name, falling back to the raw term id. */
  packageCodeName(packageCode: string): string {
    if (!packageCode) { return ''; }
    const term = (this._taxonomyService.package_code ?? [])
      .find((code: any) => code?.id?.toString() === packageCode.toString());
    return term?.name ?? packageCode;
  }

  /**
   * Whether this row's actions are offered at all. System-wide (franchise)
   * events are editable/deletable by super admins only. The server enforces
   * the same rule, so hiding the buttons is UX, not the boundary.
   */
  canManage(event: ManagedMiscEvent): boolean {
    return event?.systemWide ? this._permissionsService.isSuperAdmin() : true;
  }

  /** A referenced event cannot be deleted; say so before the click, not after. */
  isDeleteBlocked(event: ManagedMiscEvent): boolean {
    return (event?.linkedPackages ?? 0) > 0 || (event?.linkedDeductions ?? 0) > 0;
  }

  deleteBlockedTooltip(event: ManagedMiscEvent): string {
    if (!this.isDeleteBlocked(event)) { return ''; }
    const packages = event?.linkedPackages ?? 0;
    const deductions = event?.linkedDeductions ?? 0;
    return `In use by ${packages} package(s) and ${deductions} deduction(s). Unlink them first.`;
  }

  /**
   * Open the shared create/edit dialog for a new event, seeded with the
   * package code the report was already filtered to. The create form has
   * defaulted to it since the selector lived in the filter row, and it is
   * almost always the code the new event wants.
   */
  openCreateEventDialog(): void {
    this._dialogService.openDialog(MiscEventEntityComponent, 'defaultWithData', {
      data: {
        EntityID: null,
        eckType: 'misc_event',
        bundle: 'misc_event',
        action: 'create',
        fieldsData: { field_package_code: this._injectedDialogData?.['data']?.['field_package_code'] ?? null },
      },
    }).afterClosed().subscribe((result: any) => {
      if (!result) { return; }
      this.changed = true;
      this.loadEvents();
    });
  }

  /**
   * Reuse the existing create/edit dialog rather than duplicating the form.
   * It is seeded with the parsed row shape (id/title/packageCode/dateStart/
   * dateEnd/systemWide), not the raw list-endpoint shape.
   */
  openEditEventDialog(event: ManagedMiscEvent): void {
    if (!event || !this.canManage(event)) { return; }

    this._dialogService.openDialog(MiscEventEntityComponent, 'defaultWithData', {
      data: {
        EntityID: event.id,
        eckType: 'misc_event',
        bundle: 'misc_event',
        action: 'edit',
        fieldsData: {
          id: event.id,
          title: event.title,
          packageCode: event.packageCode,
          dateStart: event.dateStart,
          dateEnd: event.dateEnd,
          systemWide: event.systemWide,
        },
      },
    }).afterClosed().subscribe((result: any) => {
      if (!result) { return; }
      this.changed = true;
      this.loadEvents();
    });
  }

  startDelete(event: ManagedMiscEvent): void {
    if (!event || !this.canManage(event) || this.isDeleteBlocked(event)) { return; }
    this.errorMessage = '';
    this.pendingDeleteId = event.id;
    this.renderConfirmRow();
  }

  cancelDelete(): void {
    this.pendingDeleteId = null;
    this.renderConfirmRow();
  }

  /**
   * Which row shows its confirmation is decided by a matRowDef `when`
   * predicate, and mat-table only re-runs those when its data is re-emitted.
   * So toggling pendingDeleteId has to poke the data sources.
   */
  renderConfirmRow(): void {
    this.franchiseDataSource.data = this.franchiseDataSource.data;
    this.studioDataSource.data = this.studioDataSource.data;
  }

  /**
   * Delete straight from this dialog: the counts are already on screen and the
   * sibling manage dialogs confirm inline rather than routing a destructive
   * action through a second form. The 409 branch stays because the counts come
   * from the last list fetch and can be stale.
   */
  confirmDelete(event: ManagedMiscEvent): void {
    if (!event || this.deletingId) { return; }
    this.deletingId = event.id;
    this.errorMessage = '';

    this._drupalRESTService.httpDELETE(`/api/misc-event/${event.id}`)
      .subscribe(
        () => {
          this.deletingId = null;
          this.pendingDeleteId = null;
          this.changed = true;
          this._snackBar.open('Event deleted.', 'OK', { duration: 3000 });
          this.loadEvents();
        },
        (error: any) => {
          this.deletingId = null;
          this.pendingDeleteId = null;
          this.renderConfirmRow();

          const body = error?.error;
          if (error?.status === 409 && body?.error === 'in_use') {
            const packages = Number(body?.linked_packages ?? 0);
            const deductions = Number(body?.linked_deductions ?? 0);
            const message = `This event can't be deleted: ${packages} package${packages === 1 ? '' : 's'}`
              + ` and ${deductions} deduction${deductions === 1 ? '' : 's'} still reference it.`;
            this.errorMessage = message;
            this._snackBar.open(message, 'OK', { duration: 8000 });
            // The on-screen counts were stale; refresh so the button disables,
            // without wiping the refusal that says what to unlink.
            this.loadEvents(true);
            return;
          }

          this.errorMessage = body?.message || 'Failed to delete the event.';
        }
      );
  }

  closeDialog(): void {
    this.dialogRef.close(this.closeData);
  }
}
